import { STREAM_SAFETY_POLL_INTERVAL_MS } from './game-stream'

export const STALE_SNAPSHOT_MS = 10_000

export const STREAM_STALE_SNAPSHOT_MS
  = STREAM_SAFETY_POLL_INTERVAL_MS * 2

export type WaitingDelivery
  = | 'streaming'
    | 'reconnecting'
    | 'fallback'
    | 'polling'

export type WaitingKind
  = | 'aiActing'
    | 'completed'
    | 'error'
    | 'generic'
    | 'hiddenNight'
    | 'fallback'
    | 'otherHuman'
    | 'reconnecting'
    | 'stale'
    | 'yourTurn'

export interface WaitingInput {
  gameStatus?: string | null
  hasNextInput?: boolean
  delivery?: WaitingDelivery
  isFetchFailing?: boolean
  lastSuccessAgeMs?: number | null
  shouldHideSubphaseDetail?: boolean
  thinkingSeatNames?: string[]
  waitingOnNames?: string[]
}

export interface WaitingPresentation {
  i18nKey: string
  i18nVars: Record<string, string>
  kind: WaitingKind
  politeLiveRegion: true
}

function safeNames(names: string[] | undefined, allowed: boolean): string[] {
  if (!allowed || !Array.isArray(names))
    return []
  return names.filter(name => typeof name === 'string' && name.trim() !== '')
}

export function resolveWaitingPresentation(
  input: WaitingInput = {},
): WaitingPresentation {
  const hideDetail = input.shouldHideSubphaseDetail === true

  const present = (
    kind: WaitingKind,
    i18nKey: string,
    i18nVars: Record<string, string> = {},
  ): WaitingPresentation => ({
    i18nKey,
    i18nVars,
    kind,
    politeLiveRegion: true,
  })

  if (input.gameStatus === 'completed') {
    return present('completed', 'play.waitingState.completed')
  }
  if (input.gameStatus === 'error') {
    return present('error', 'play.waitingState.error')
  }

  if (input.isFetchFailing === true)
    return present('stale', 'play.waitingState.stale')

  const staleAge = input.lastSuccessAgeMs
  const staleAfter = input.delivery === 'streaming'
    ? STREAM_STALE_SNAPSHOT_MS
    : STALE_SNAPSHOT_MS
  const isStale
    = typeof staleAge === 'number'
      && Number.isFinite(staleAge)
      && staleAge > staleAfter
  if (isStale)
    return present('stale', 'play.waitingState.stale')

  if (input.hasNextInput === true) {
    return present('yourTurn', 'play.waitingState.yourTurn')
  }

  if (input.delivery === 'reconnecting')
    return present('reconnecting', 'play.waitingState.reconnecting')
  if (input.delivery === 'fallback')
    return present('fallback', 'play.waitingState.fallback')

  const waitingOn = safeNames(input.waitingOnNames, !hideDetail)
  const thinking = safeNames(input.thinkingSeatNames, !hideDetail)

  if (waitingOn.length > 0) {
    return present('otherHuman', 'play.waitingState.otherHuman', {
      names: waitingOn.join(', '),
    })
  }

  if (hideDetail)
    return present('hiddenNight', 'play.waitingState.hiddenNight')

  if (thinking.length === 1)
    return present('aiActing', 'play.waitingState.aiActing', { name: thinking[0]! })
  if (thinking.length > 1)
    return present('aiActing', 'play.waitingState.aiActingMany')

  return present('generic', 'play.waitingState.generic')
}

export const WAITING_STATE_I18N_KEYS = [
  'play.waitingState.aiActing',
  'play.waitingState.aiActingMany',
  'play.waitingState.completed',
  'play.waitingState.error',
  'play.waitingState.fallback',
  'play.waitingState.generic',
  'play.waitingState.hiddenNight',
  'play.waitingState.otherHuman',
  'play.waitingState.reconnecting',
  'play.waitingState.stale',
  'play.waitingState.yourTurn',
] as const
